import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormikErrors } from 'formik';
import { SelectOption } from '@iddataweb/idw-common-ui/types';
import { getAXNUIAttributeLabel } from "@iddataweb/idw-common-ui/utils";
import { AttributeStatusTypes } from '@enums/attribute-status-types';
import { AttributeFormState } from '@idw-types/state/attribute-form-state';
import { getLanguageContent } from '@redux/selectors/language-selectors';
import { getLocalizeStrings } from '@redux/selectors/verification-configuration-selectors';
import { Logger } from '@utils/logger';
import { AXNUISelectInput } from '@iddataweb/idw-common-ui/containers';
import styles from "./picker-attribute-input.less";

type PickerAttributeInputProps = {
    attribute: AttributeFormState;
    fieldName: string;
    fieldShouldBeHighlighted: boolean;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    onAddNewSubmitValue: (data: any) => void;
    onSetPickerValue: (data: any) => void;
    isSubmitting: boolean;
};

export const PickerAttributeInput = (props: PickerAttributeInputProps) => {
    const {
        attribute,
        fieldName,
        setFieldValue,
        setFieldTouched,
        onAddNewSubmitValue,
        onSetPickerValue,
        isSubmitting,
    } = props;

    const [selectedOption, setSelectedOption] = useState<SelectOption>(null);
    const content = useSelector(getLanguageContent);
    const localizeStrings = useSelector(getLocalizeStrings);

    const isFieldDisabled = isSubmitting || attribute.isDisabled;

    useEffect(() => {
        if (attribute.pickerData.id) {
            const option = attribute.attrList.find((option) => `${option.attribute.id}` === `${attribute.pickerData.id}`);
            if (option?.attribute) {
                setSelectedOption({ label: option.attribute.masked, value: `${option.attribute.id}` });
            }
        }
    }, [attribute.pickerData.id]);

    useEffect(() => {
        if (attribute.status === AttributeStatusTypes.PICKER && !attribute.pickerData.id) {
            Logger.info('[PickerAttributeInput] set picker value to ', attribute.pickerDefaultValue);
            onSetPickerValue({
                pickerData: {
                    id: attribute.pickerDefaultValue
                }
            });
        }
    }, [attribute.pickerDefaultValue, attribute.status, attribute.pickerData.id]);

    const handleChange = async (newValue: SelectOption) => {
        const option = attribute.attrList.find((option) => `${option.attribute.id}` === `${newValue.value}`);
        if (option?.attribute) {
            await setFieldValue(fieldName, option.attribute.masked);
            setFieldTouched(fieldName, true, true);
            Logger.info(`[PickerAttributeInput] Change picker value to ${option.attribute.masked}`);
        }
        onSetPickerValue({ pickerData: { id: newValue.value } });
    };

    const addNewValue = async (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();

        const attrListAttribute = attribute.attrList.find(attrListAttr => attrListAttr.attribute?.id === attribute.pickerData?.id);
        if (!attribute.masked && attrListAttribute) {
            onAddNewSubmitValue(attrListAttribute.attribute.valuesMap);
            await setFieldValue(fieldName, attrListAttribute.attribute.masked);
            setFieldTouched(fieldName, true, true);
        } else {
            onAddNewSubmitValue({});
            await setFieldValue(fieldName, '');
            setFieldTouched(fieldName, false, true);
        }
        
    };

    const shouldDisplayAddNew = useMemo(() => {
        return AttributeStatusTypes.PICKER === attribute.status && attribute.isEditable;
    }, [attribute.status, attribute.isEditable]);

    const options = useMemo(() => {
        return attribute.attrList.map((previousAttributeOption) => ({
            label: previousAttributeOption.attribute.masked,
            value: `${previousAttributeOption.attribute.id}`
        }));
    }, [attribute.attrList]);

    const attributeLabel = useMemo(() => {
        return getAXNUIAttributeLabel(
            localizeStrings,
            content.labels.attributes[attribute.type],
            attribute.label
        )
    }, [
        localizeStrings,
        content.labels.attributes[attribute.type],
        attribute.label
    ]);

    return (
        <div className={styles.pickerAttributeInput}>
            <AXNUISelectInput
                controlId={`attr${attribute.id}-picker-select`}
                automationId={`attr${attribute.id}-picker-select`}
                required={!attribute.optional}
                ariaLabel={""}
                isClearable={false}
                isSearchable={false}
                title={""}
                placeholder={""}
                selectedOption={selectedOption}
                disabled={isFieldDisabled}
                options={options}
                onBlur={() => { }}
                onChange={handleChange}
            />
            {shouldDisplayAddNew && (
                <div className={styles.linkContainer}>
                    <a
                        data-testid="add-new-link"
                        id={`attr${attribute.id}-add-new-link`}
                        href={""}
                        className={styles.manualLink}
                        onClick={addNewValue}>
                        {content.form.pickerInput.addNew} {attributeLabel}
                    </a>
                </div>
            )}
        </div>
    );
};